import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, ArrowRight, Plus, RotateCcw } from "lucide-react";
import { sendIntakeMessage } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const newSession = () => (crypto?.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random()}`);

function Thinking() {
  return (
    <div className="flex items-center gap-1.5 px-4 py-3">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-2 h-2 rounded-full bg-forest/60"
          style={{ animation: `pulse 1s ease-in-out ${i * 0.18}s infinite` }}
        />
      ))}
      <style>{`@keyframes pulse{0%,100%{opacity:.3;transform:scale(.85)}50%{opacity:1;transform:scale(1.1)}}`}</style>
    </div>
  );
}

export default function Intake() {
  const [sessionId, setSessionId] = useState(newSession);
  const [messages, setMessages] = useState([]); // {role, text, recommendations?}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const scrollRef = useRef(null);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const scrollToBottom = () => {
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 50);
  };

  const pushAndSend = async (userText, isFirst = false) => {
    setLoading(true);
    if (!isFirst) setMessages((m) => [...m, { role: "user", text: userText }]);
    scrollToBottom();
    try {
      const res = await sendIntakeMessage(sessionId, userText);
      setMessages((m) => [...m, { role: "ai", text: res.reply, recommendations: res.recommendations || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: "I'm having trouble connecting right now. Please try again in a moment." }]);
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  };

  const start = () => {
    setStarted(true);
    pushAndSend("", true);
  };

  const send = () => {
    const t = input.trim();
    if (!t || loading) return;
    setInput("");
    pushAndSend(t);
  };

  const restart = () => {
    setSessionId(newSession());
    setMessages([]);
    setStarted(false);
  };

  useEffect(() => { scrollToBottom(); }, [messages, loading]);

  return (
    <div data-testid="intake-page" className="bg-bone pt-[72px] min-h-screen">
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-forest bg-sage px-4 py-2 rounded-full">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> AI Assessment
          </span>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight text-ink mt-5 leading-[1.05]">
            Meet Vera, your concierge.
          </h1>
          <p className="text-stone mt-4 max-w-lg mx-auto leading-relaxed">
            A few quick questions and Vera will build a peptide protocol matched to your goals.
          </p>
        </div>

        <div className="tracing-beam rounded-[1.75rem] bg-bone p-1">
          <div className="rounded-[1.6rem] bg-bone border border-line overflow-hidden flex flex-col h-[62vh] min-h-[440px]">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-line bg-sand/60">
              <div className="w-9 h-9 rounded-full bg-forest flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-semibold text-sm text-ink">Vera</p>
                <p className="text-xs text-forest">AI Wellness Concierge</p>
              </div>
              {started && (
                <button data-testid="restart-btn" onClick={restart} className="ml-auto text-stone hover:text-forest transition-colors flex items-center gap-1.5 text-xs font-semibold">
                  <RotateCcw className="w-3.5 h-3.5" /> Restart
                </button>
              )}
            </div>

            <div ref={scrollRef} data-testid="chat-scroll" className="flex-1 overflow-y-auto px-5 py-6 space-y-4">
              {!started ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-5">
                  <div className="w-16 h-16 rounded-full bg-sage flex items-center justify-center">
                    <Sparkles className="w-7 h-7 text-forest" strokeWidth={1.25} />
                  </div>
                  <p className="text-stone max-w-xs">Ready when you are. This takes about two minutes.</p>
                  <button
                    data-testid="start-chat-btn"
                    onClick={start}
                    className="inline-flex items-center gap-2 rounded-full bg-forest text-[#14100F] px-7 py-3.5 text-base font-semibold hover:bg-forest-dark active:scale-95 transition-[background-color,transform] duration-200"
                  >
                    Begin assessment <ArrowRight className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              ) : (
                <AnimatePresence initial={false}>
                  {messages.map((m, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35 }}
                    >
                      <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <p
                          data-testid={`chat-bubble-${m.role}`}
                          className={`max-w-[82%] text-[15px] leading-relaxed px-4 py-3 rounded-2xl whitespace-pre-wrap ${
                            m.role === "user"
                              ? "bg-sand text-ink rounded-br-md"
                              : "bg-sage text-forest-dark rounded-bl-md"
                          }`}
                        >
                          {m.text}
                        </p>
                      </div>

                      {m.recommendations && m.recommendations.length > 0 && (
                        <div data-testid="recommendations" className="mt-4 space-y-3">
                          {m.recommendations.map((p) => (
                            <div key={p.slug} className="flex items-center gap-4 rounded-xl border border-line bg-bone p-3">
                              <Link to={`/product/${p.slug}`} className="w-16 h-20 rounded-lg overflow-hidden bg-sand flex-shrink-0">
                                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                              </Link>
                              <div className="flex-1 min-w-0">
                                <Link to={`/product/${p.slug}`} className="font-display text-lg text-ink leading-tight hover:text-forest transition-colors">
                                  {p.name}
                                </Link>
                                <p className="text-xs text-stone">{p.subtitle}</p>
                                <p className="text-sm font-semibold text-ink mt-0.5">${p.price.toFixed(0)}</p>
                              </div>
                              <button
                                data-testid={`rec-add-${p.slug}`}
                                onClick={() => { addItem(p); toast.success(`${p.name} added to cart`); }}
                                className="w-10 h-10 rounded-full bg-forest text-[#14100F] flex items-center justify-center hover:bg-forest-dark transition-colors flex-shrink-0"
                                aria-label={`Add ${p.name}`}
                              >
                                <Plus className="w-5 h-5" />
                              </button>
                            </div>
                          ))}
                          <button
                            data-testid="rec-checkout-btn"
                            onClick={() => navigate("/checkout")}
                            className="w-full mt-1 rounded-full border border-forest text-forest py-3 text-sm font-semibold hover:bg-sage transition-colors"
                          >
                            Review cart & checkout
                          </button>
                        </div>
                      )}
                    </motion.div>
                  ))}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-sage rounded-2xl rounded-bl-md">
                        <Thinking />
                      </div>
                    </div>
                  )}
                </AnimatePresence>
              )}
            </div>

            {started && (
              <div className="border-t border-line px-4 py-3 bg-bone">
                <div className="flex items-center gap-2">
                  <input
                    data-testid="chat-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder="Type your answer…"
                    disabled={loading}
                    className="flex-1 bg-sand/50 border border-line rounded-full px-5 py-3 text-[15px] text-ink placeholder:text-stone/70 focus:outline-none focus:ring-1 focus:ring-forest disabled:opacity-60"
                  />
                  <button
                    data-testid="chat-send-btn"
                    onClick={send}
                    disabled={loading || !input.trim()}
                    className="w-12 h-12 rounded-full bg-forest text-[#14100F] flex items-center justify-center hover:bg-forest-dark active:scale-95 transition-[background-color,transform] disabled:opacity-40"
                    aria-label="Send"
                  >
                    <Send className="w-5 h-5" strokeWidth={1.75} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-stone mt-5 max-w-md mx-auto leading-relaxed">
          Vera provides wellness guidance, not medical advice. Every recommendation is reviewed by a licensed provider before any order ships.
        </p>
      </div>
    </div>
  );
}
